import React, { useEffect, useRef, useState } from 'react';
import {
  XMarkIcon, ClipboardDocumentIcon, ShareIcon, ChatBubbleLeftRightIcon, ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Formats de l'affiche (gabarit « Bourses d'idées »).
const FORMATS = [
  { id: 'carre', label: 'Carré', hint: 'LinkedIn, WhatsApp' },
  { id: 'portrait', label: 'Portrait', hint: 'Fil LinkedIn' },
  { id: 'story', label: 'Story', hint: 'Statut WhatsApp' },
];

// Copie avec repli pour les navigateurs sans API Clipboard (HTTP, anciens navigateurs).
const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(area);
  if (!ok) throw new Error('copy failed');
};

// LinkedIn et Facebook n'ont pas d'icône dans @heroicons : logos dessinés ici.
const LinkedInIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M24 12.07C24 5.41 18.63 0 12 0S0 5.41 0 12.07c0 6.02 4.39 11.02 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.09 24 18.09 24 12.07z" />
  </svg>
);

const fetchPoster = async (url, format) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  return new File([blob], `appel-a-projets-${format}.png`, { type: 'image/png' });
};

/**
 * Aperçu modifiable du texte de publication d'un appel à projets, avec son affiche.
 * Le lien est inclus dans le texte : un seul bloc à coller, quel que soit le réseau.
 * posterUrlFor(format) renvoie l'adresse de l'affiche du format choisi ; shareUrl est la page
 * de partage (aperçu Innovactive360), seule chose que Facebook accepte de recevoir.
 */
const ShareCallModal = ({ initialText, title, shareUrl, posterUrlFor, onClose }) => {
  const [text, setText] = useState(initialText);
  const [format, setFormat] = useState('carre');
  // État lié à l'URL : changer de format repart en chargement sans effet intermédiaire.
  const [posterDone, setPosterDone] = useState({ url: null, ok: false });
  const [busy, setBusy] = useState(false);
  const areaRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const posterUrl = posterUrlFor(format);
  const posterState = posterDone.url !== posterUrl ? 'loading' : (posterDone.ok ? 'ready' : 'error');

  // Une seule fois à l'ouverture (onClose est recréée à chaque rendu du parent).
  useEffect(() => {
    const area = areaRef.current;
    if (area) {
      area.focus();
      area.setSelectionRange(area.value.length, area.value.length);
      area.scrollTop = 0;
    }
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleCopy = async () => {
    try {
      await copyText(text);
      toast.success('Texte et lien copiés : collez-les dans votre publication');
    } catch {
      areaRef.current?.select();
      toast.error('Copie impossible : sélectionnez le texte et copiez-le manuellement');
    }
  };

  const downloadPoster = async () => {
    const file = await fetchPoster(posterUrl, format);
    const href = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = href;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      await downloadPoster();
    } catch {
      toast.error("L'image n'a pas pu être téléchargée. Réessayez.");
    } finally {
      setBusy(false);
    }
  };

  // Téléphone : l'affiche part avec le texte quand l'application choisie accepte une image,
  // sinon le texte seul (son premier lien affiche l'aperçu Innovactive360).
  const handleNativeShare = async () => {
    setBusy(true);
    try {
      let shared = false;
      try {
        const file = await fetchPoster(posterUrl, format);
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ title, text, files: [file] });
          shared = true;
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
      if (!shared) await navigator.share({ title, text });
    } catch (error) {
      if (error?.name !== 'AbortError') handleCopy();
    } finally {
      setBusy(false);
    }
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
  // LinkedIn : la fenêtre de publication s'ouvre avec le texte (paramètre text, non garanti par
  // LinkedIn : le texte est aussi copié). Un lien seul n'y donne qu'une petite vignette : pour
  // l'affiche en grand, elle est téléchargée et la personne l'ajoute à sa publication.
  const linkedinUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`;
  // Facebook ne reçoit que le lien : l'aperçu montre l'affiche au format lien (1200 × 630).
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;

  // La fenêtre du réseau s'ouvre par le lien lui-même (pas de blocage des fenêtres) ;
  // copie et téléchargement se font en même temps.
  const preparePost = async (reseau, withPoster) => {
    const [copied, downloaded] = await Promise.allSettled([
      copyText(text),
      withPoster ? downloadPoster() : Promise.resolve(),
    ]);
    const okText = copied.status === 'fulfilled';
    const okPoster = withPoster && downloaded.status === 'fulfilled';
    const etapes = [
      okPoster && "ajoutez l'affiche téléchargée (bouton Photo)",
      okText && 'collez le texte si besoin',
    ].filter(Boolean);
    const message = etapes.length
      ? `Dans ${reseau} : ${etapes.join(', puis ')}.`
      : `Dans ${reseau}, ajoutez votre texte à la publication.`;
    toast.success(message, { duration: 8000 });
  };
  const button = 'inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60';

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-600 bg-opacity-50 p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-call-title"
        className="w-full max-w-3xl rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h3 id="share-call-title" className="text-lg font-semibold text-gray-900">
            Partager cet appel à projets
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Fermer">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="grid gap-5 px-5 py-4 md:grid-cols-2">
          <div>
            <label htmlFor="share-call-text" className="mb-2 block text-sm text-gray-600">
              Texte de votre publication (modifiable). Le premier lien affiche l'aperçu Innovactive360.
            </label>
            <textarea
              id="share-call-text"
              ref={areaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={13}
              className="w-full rounded-lg border border-gray-300 p-3 text-sm text-gray-800 focus:border-primary-500 focus:ring-primary-500"
            />
            <button
              type="button"
              onClick={() => setText(initialText)}
              disabled={text === initialText}
              className="mt-1 text-xs text-primary-600 hover:text-primary-700 disabled:invisible"
            >
              Revenir au texte proposé
            </button>
          </div>

          <div>
            <p className="mb-2 text-sm text-gray-600">Affiche à joindre</p>
            <div className="mb-3 grid grid-cols-3 gap-2" role="radiogroup" aria-label="Format de l'affiche">
              {FORMATS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  role="radio"
                  aria-checked={format === f.id}
                  onClick={() => setFormat(f.id)}
                  className={`rounded-lg border px-2 py-1.5 text-center text-xs ${format === f.id
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  <span className="block font-semibold">{f.label}</span>
                  <span className="block text-[11px] text-gray-500">{f.hint}</span>
                </button>
              ))}
            </div>
            <div className="flex h-72 items-center justify-center rounded-lg bg-gray-100 p-2">
              {posterState === 'error' ? (
                <p className="px-4 text-center text-sm text-gray-500">
                  L'affiche n'a pas pu être chargée. Le texte et le lien restent partageables.
                </p>
              ) : (
                <img
                  key={posterUrl}
                  src={posterUrl}
                  alt={`Affiche de l'appel à projets, format ${format}`}
                  onLoad={() => setPosterDone({ url: posterUrl, ok: true })}
                  onError={() => setPosterDone({ url: posterUrl, ok: false })}
                  className={`max-h-full max-w-full rounded shadow ${posterState === 'ready' ? '' : 'opacity-0'}`}
                />
              )}
            </div>
            <button
              type="button"
              onClick={handleDownload}
              disabled={busy || posterState !== 'ready'}
              className={`${button} mt-3 w-full`}
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              Télécharger l'affiche
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2 border-t border-gray-200 px-5 py-4 sm:flex-row sm:flex-wrap sm:justify-end">
          {canNativeShare && (
            <button type="button" onClick={handleNativeShare} disabled={busy} className={button}>
              <ShareIcon className="h-5 w-5" />
              Partager avec l'affiche
            </button>
          )}
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className={button}>
            <ChatBubbleLeftRightIcon className="h-5 w-5" />
            WhatsApp
          </a>
          {shareUrl && (
            <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" onClick={() => preparePost('LinkedIn', true)} className={button}>
              <LinkedInIcon className="h-5 w-5" />
              LinkedIn
            </a>
          )}
          {shareUrl && (
            <a href={facebookUrl} target="_blank" rel="noopener noreferrer" onClick={() => preparePost('Facebook', false)} className={button}>
              <FacebookIcon className="h-5 w-5" />
              Facebook
            </a>
          )}
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            <ClipboardDocumentIcon className="h-5 w-5" />
            Copier le texte et le lien
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareCallModal;
